

// Règles des bons de livraison : lignes livrables, lignes CNO, occurrences de livraison planifiées et recherche.

export const STATUTS_BON = [
    { key: "TRAITE",       label: "Traité" },
    { key: "EN_COMMANDE",  label: "En commande" },
    { key: "EN_RUPTURE",   label: "Rupture de stock" },
    { key: "EN_LIVRAISON", label: "En livraison" },
    { key: "LIVRE",        label: "Livré" },
];

export const PRINT_GROUPING_MODES = [
    { key: "line", label: "Actuel" },
    { key: "patient", label: "Par patient" },
    { key: "medicament", label: "Par médicament" },
];

export const medicamentLineKey = (med) => med.lineKey || String(med.id);

export function isCnoLine(med) {
    return med?.lineKind === "CNO" || String(medicamentLineKey(med)).startsWith("cno:");
}

export function isPartialDeliverableLine(med) {
    return med?.lineKind === "PARTIAL_DELIVERABLE";
}

export function isDeliverySelectableLine(med) {
    return med?.statut !== "LIVRE"
        && ((!med?.lineKind || med.lineKind === "NORMAL") || isCnoLine(med) || isPartialDeliverableLine(med));
}

export function isDirectlyDeliverableLine(med) {
    return isDeliverySelectableLine(med) && med?.statut !== "EN_COMMANDE";
}

export function cnoBonFromLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return [];
    return [{
        ehpadId: "CNO",
        ehpadCode: "CNO",
        ehpadNom: "CNO",
        ehpadAddress: "",
        ehpadPhone: "",
        tourneeId: null,
        tourneeName: null,
        medicaments: lines.map(line => ({
            id: line.id,
            lineKey: `cno:${line.id}`,
            lineKind: "CNO",
            nom: line.nom,
            dosage: "",
            posologie: "",
            quantite: line.quantite,
            statut: line.statut === "TRAITE" ? "TRAITE" : "EN_LIVRAISON",
            destination: "ETIQUETAGE",
            patientNom: line.patientNom,
            patientPrenom: line.patientPrenom,
            patientAdresse: line.patientAdresse,
            patientTelephone: line.patientTelephone,
            classificationMedicament: "CNO",
        })),
    }];
}

export function dateKeyFromDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function addDaysToDateKey(key, days) {
    const date = new Date(`${key}T00:00:00`);
    date.setDate(date.getDate() + days);
    return dateKeyFromDate(date);
}

export function formatDateShort(value) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

export function deliveryPlanningMatchesEhpad(planning, ehpadId) {
    return (planning.ehpads || []).some(ehpad => Number(ehpad.id) === Number(ehpadId));
}

export function deliveryOccurrenceLabel(planning, occurrenceDate) {
    const time = planning.deliveryTime ? ` · ${String(planning.deliveryTime).slice(0, 5)}` : "";
    return `${formatDateShort(occurrenceDate)}${time} · ${planning.label}`;
}

export const DELIVERY_OCCURRENCE_WINDOW_DAYS = 30;

export const HISTORIQUE_PAGE_SIZE = 10;

export function parseSelectIdentifier(value) {
    if (!value) return null;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
}

export function buildDeliveryOccurrenceOptions(plannings, ehpadId) {
    const today = dateKeyFromDate(new Date());
    const lastVisibleDate = addDaysToDateKey(today, DELIVERY_OCCURRENCE_WINDOW_DAYS);
    const options = [];

    plannings
        .filter(planning => deliveryPlanningMatchesEhpad(planning, ehpadId))
        .forEach(planning => {
            const frequency = planning.frequency || "HEBDOMADAIRE";
            const frequencyDays = Number(planning.frequencyDays) > 0 ? Number(planning.frequencyDays) : 7;
            const overrides = new Map(
                (planning.occurrenceOverrides || [])
                    .filter(override => override.originalDate && override.movedDate)
                    .map(override => [override.originalDate, override.movedDate])
            );
            let occurrenceDate = planning.firstDeliveryDate;
            let guard = 0;

            while (occurrenceDate && guard < 320) {
                const movedDate = overrides.get(occurrenceDate);
                const displayDate = movedDate || occurrenceDate;
                if (displayDate >= today && displayDate <= lastVisibleDate) {
                    options.push({
                        planningId: planning.id,
                        occurrenceDate: displayDate,
                        label: movedDate
                            ? `${deliveryOccurrenceLabel(planning, displayDate)} · déplacée du ${formatDateShort(occurrenceDate)}`
                            : deliveryOccurrenceLabel(planning, displayDate),
                    });
                }
                if (frequency === "PONCTUELLE") return;
                if (occurrenceDate > lastVisibleDate && displayDate > lastVisibleDate) return;
                occurrenceDate = addDaysToDateKey(occurrenceDate, frequencyDays);
                guard += 1;
            }
        });

    return options.sort((a, b) => a.occurrenceDate.localeCompare(b.occurrenceDate) || a.label.localeCompare(b.label));
}

export function hasPharmacyIdentity(pharmacy) {
    const name = pharmacy?.name || pharmacy?.nom;
    return typeof name === "string" && name.trim().length > 0;
}

export function normalizeDeliverySearch(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("fr")
        .trim();
}

export function matchesDeliverySearch(medicament, query) {
    const tokens = normalizeDeliverySearch(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const searchable = normalizeDeliverySearch([
        medicament.patientNom,
        medicament.patientPrenom,
        medicament.nom,
        medicament.dosage,
    ].filter(Boolean).join(" "));
    return tokens.every(token => searchable.includes(token));
}

export function makePatch({
    statut,
    planificationValidated,
    planificationValidatedUsername,
    productionValidated,
    productionValidatedUsername,
}) {
    return Object.fromEntries(Object.entries({
        statut,
        planificationValidated,
        planificationValidatedUsername,
        productionValidated,
        productionValidatedUsername,
    }).filter(([, value]) => value !== undefined));
}
